import React from 'react';
import PropTypes from 'prop-types';
import round from 'lodash/round';
import { connect } from 'react-redux';
import a from '../../../../../redux/actions';
import s from '../../../../../redux/selectors';
import { wrap, withT } from '../../../../../lib/wrappers';
import InlineInput from '../InlineInput';
import InlineSelect from '../InlineSelect';
import units from '../../../../../lib/units';
import numericInput from '../../../../../lib/numericInput';

const carSizeOptions = t => [
  { value: 0, display: t('small car') },
  { value: 1, display: t('medium-size car') },
  { value: 2, display: t('large car') },
  { value: 3, display: t('truck / SUV') },
  { value: 4, display: t('minivan') },
];

const carAgeOptions = t => [
  { value: 0, display: t('Select age') },
  { value: 1, display: t('{{min}} to {{max}} years old', { min: 1, max: 3 }) },
  { value: 2, display: t('{{min}} to {{max}} years old', { min: 4, max: 6 }) },
  { value: 3, display: t('{{min}} to {{max}} years old', { min: 7, max: 9 }) },
  { value: 4, display: t('{{min}} to {{max}} years old', { min: 10, max: 12 }) },
  { value: 5, display: t('{{min}} to {{max}} years old', { min: 13, max: 15 }) },
  { value: 6, display: t('{{min}} to {{max}} years old', { min: 16, max: 18 }) },
  { value: 7, display: t('{{min}} to {{max}} years old', { min: 19, max: 21 }) },
];

const daysPerOptions = t => [
  { value: 0, display: t('week') },
  { value: 1, display: t('month') },
];

const fuelDensity = (isMetric, t) => ({
  massUnit: isMetric ? t('kg', { ns: 'units' }) : t('lbs', { ns: 'units' }),
  fuelConsumptionUnit: units.fuelConsumptionUnit(t)(isMetric, false),
  density: numericInput.format(
    (isMetric ? units.lbsPerGalToKgPerL(19.4, 1) : 19.4),
    { decimalPlaces: 1 },
  ),
});

const depreciation = (isMetric, t) => ({
  distanceUnit: isMetric ? t('km', { ns: 'units' }) : t('mi', { ns: 'units' }),
  value: isMetric ? round(0.05 / units.KM_PER_MI, 2) : '0.05',
});

const CarpoolToWork = ({
  t,
  isMetric,
  widgetSource,
}) => (
  <div className="action-details">
    <div className="action-details__form">
      <p>
        {t('I usually drive alone in a')}
        <InlineSelect
          options={carSizeOptions(t)}
          apiKey={'input_takeaction_carpool_to_work_category'}
        />
        {t('that is')}
        <InlineSelect
          options={carAgeOptions(t)}
          apiKey={'input_takeaction_carpool_to_work_depvehu'}
        />&nbsp;
        {t('and gets')}
        <InlineInput
          apiKey={'input_takeaction_carpool_to_work_mpgvehu'}
        />
        {t('{{fuelEfficiencyUnit}}. I pledge to carpool', {
          fuelEfficiencyUnit: units.fuelEfficiencyUnit(t)(isMetric, false),
        })}
        <InlineInput
          maxLength={1}
          apiKey={'input_takeaction_carpool_to_work_days'}
        />
        {t('days per')}
        <InlineSelect
          options={daysPerOptions(t)}
          apiKey={'input_takeaction_carpool_to_work_daysper'}
        />
        {t('to work, which is a')}
        <InlineInput
          apiKey={'input_takeaction_carpool_to_work_dist'}
        />
        {t('{{distanceUnit}} trip each way. I will carpool in a vehicle that gets', {
          distanceUnit: isMetric ? t('kilometer') : t('mile'),
        })}
        <InlineInput
          apiKey={'input_takeaction_carpool_to_work_mpgvehc'}
        />
        {t('{{fuelEfficiencyUnit}} with a total of', {
          fuelEfficiencyUnit: units.fuelEfficiencyUnit(t)(isMetric, false),
        })}
        <InlineInput
          maxLength={2}
          apiKey={'input_takeaction_carpool_to_work_passenger'}
        />
        {t('people (including the driver). I will save')}
        <InlineInput
          currency
          apiKey={'input_takeaction_carpool_to_work_fees'}
        />
        {t('per trip on parking and tolls.')}
      </p>
    </div>
    <div className="action-details__facts">
      {t('Get in the Fast Lane! One of the major benefits of carpooling is that you get access to the carpool-only lanes on the freeway.')}
    </div>
    {widgetSource !== 'ontario' && <div className="action-details__links">
      <a
        href="http://www.coolcalifornia.org/funding-wizard#search//%255BZipcode%253D%253D94720%255D%255BCategory%253D%253DTransportation%255D%255BEligible+Applicants%253D%253DIndividual%255D/false/none/false/1/12"
        target="_blank"
        rel="noopener noreferrer"
      >{t('Find rebates and incentives for this action')}</a>
    </div>}
    <div className="action-details__assumptions">
      <div className="action-details__assumptions__header">{t('Assumptions')}</div>
      <ul>
        <li>
          {t('Price of gasoline:')}
          <InlineInput
            currency
            apiKey="input_takeaction_assumption_price_of_gasoline"
          />
          {t('per {{volumeUnit}}', { volumeUnit: isMetric ? t('L', { ns: 'units' }) : t('gal', { ns: 'units' }) })}
        </li>
        <li>
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: t('{{density}} {{massUnit}} CO<sub>2</sub> per {{fuelConsumptionUnit}}', fuelDensity(isMetric, t)),
            }}
          />
        </li>
        <li>
          {t('{{value}}% well-to-pump emissions', { value: 20 })}
        </li>
        <li>
          {
            // eslint-disable-next-line
            t('${{value}} depreciation per {{distanceUnit}} driven', depreciation(isMetric, t))
          }
        </li>
      </ul>
    </div>
  </div>
);

CarpoolToWork.propTypes = {
  // wrapped
  t: PropTypes.func.isRequired,
  // connected props
  isMetric: PropTypes.bool.isRequired,
  widgetSource: PropTypes.string.isRequired,
};

const mapStateToProps = (state, ownProps) => [].reduce((resultProps, resultKey) => ({
  ...resultProps,
  [resultKey]: s.results(resultKey)(state, ownProps),
}), {
  isMetric: s.isMetric(state),
  widgetSource: s.widgetSource(state),
});

const mapDispatchToProps = (_dispatch, _ownProps) => ({});

export default wrap(
  withT('carpoolToWork'),
  connect(mapStateToProps, mapDispatchToProps),
  CarpoolToWork,
);
